import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import SeeAllLink from '@/components/ui/SeeAllLink'
import TitleRow from '@/components/ui/TitleRow'
import { useSuspenseCourseHome, useSuspenseCourseList } from '@/features/courses/api/queries'
import CourseCard from '@/features/courses/components/CourseCard'
import { isLevel, levelLabel, levelsOf } from '@/features/courses/lib/level'
import type { CourseCard as CourseCardData } from '@/schemas/courses'
import ContinueLearningShelf from '@/screens/courses/components/home/ContinueLearningShelf'
import CourseFilters, { ALL_LEVELS } from '@/screens/courses/components/home/CourseFilters'
import CourseResults from '@/screens/courses/components/home/CourseResults'
import CoursesHomeSkeleton from '@/screens/courses/components/home/CoursesHomeSkeleton'
import FreeToWatchShelf from '@/screens/courses/components/home/FreeToWatchShelf'
import CourseShelf from '@/screens/courses/components/shelf/CourseShelf'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import { SHELF_MAX } from '@/screens/courses/components/shelf/shelf-box'
import { coursesSearchSchema, loadCourses } from '@/screens/courses/CoursesScreen.loader'

const TITLE = 'Courses'

/**
 * The section tab strip names this page from `lg` up, so its `h1` is
 * screen-reader only there; below `lg` there is no strip and it shows.
 */
const HEADER = <TitleRow title={TITLE} />

function CoursesPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading courses…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <CoursesHomeSkeleton />
    </ScreenLayout>
  )
}

function CoursesError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} fullWidth header={HEADER} title="Courses did not load" />
}

export const Route = createFileRoute('/_app/courses')({
  validateSearch: coursesSearchSchema,
  loader: loadCourses,
  pendingComponent: CoursesPending,
  errorComponent: CoursesError,
  component: CoursesScreen,
})

/**
 * The level every course on a shelf shares, as the label the level filter
 * reads — what "See all" opens. `null` for a mixed shelf (Going deeper is
 * Intermediate and Advanced together): no single listing stands for it.
 */
const sharedLevel = (courses: readonly CourseCardData[]): string | null => {
  const [first] = courses
  if (first?.level == null) return null
  const label = levelLabel(first.level)
  return courses.every((course) => isLevel(course, label)) ? label : null
}

interface CardShelfProps {
  title: string
  courses: readonly CourseCardData[]
}

/**
 * A shelf of the first few courses, "See all" opening the level listing only
 * when the shelf is one level and has more than it shows — anything else
 * would link to a page that adds nothing, or to one that is not the shelf.
 */
function CardShelf({ title, courses }: CardShelfProps) {
  if (courses.length === 0) return null
  const level = courses.length > SHELF_MAX ? sharedLevel(courses) : null
  return (
    <CourseShelf
      title={title}
      action={
        level === null ? undefined : (
          <SeeAllLink to="/courses" search={{ level }}>
            See all
          </SeeAllLink>
        )
      }
    >
      {courses.slice(0, SHELF_MAX).map((course) => (
        <CourseCard key={course.id} course={course} />
      ))}
    </CourseShelf>
  )
}

/**
 * Learn · Courses — the hub. The member's own progress first (Continue
 * learning), a free sample, then the catalog's two curated shelves
 * (`home/`'s Start here and Going deeper). A shelf with nothing in it does
 * not appear.
 *
 * A level or a search swaps the shelves for the grid of everything that
 * matches (`CourseResults`): a filter that left the shelves in place would
 * filter nothing a member could see. The level is in the URL (a shelf's "See
 * all" links to it); the search text is the box's own, since it narrows a
 * list already on screen and is not a place to come back to.
 *
 * `home` and the catalog are both MAIN, suspended together by the loader, so
 * every shelf is already right on the first paint — nothing here grows or
 * collapses once the page has committed.
 */
function CoursesScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const home = useSuspenseCourseHome().data
  const courses = useSuspenseCourseList().data
  const [term, setTerm] = useState('')

  const levels = levelsOf(courses)
  // A level in the URL that the catalog does not have (a stale link) is no filter.
  const level = levels.find((label) => label.toLowerCase() === search.level?.toLowerCase())

  const needle = term.trim().toLowerCase()
  const filtering = level !== undefined || needle !== ''
  const matches = filtering
    ? courses.filter(
        (course) =>
          (level === undefined || isLevel(course, level)) &&
          course.title.toLowerCase().includes(needle),
      )
    : courses

  const setLevel = (next: string) => {
    void navigate({
      search: (prev) => ({ ...prev, level: next === ALL_LEVELS ? undefined : next }),
      replace: true,
    })
  }

  const clear = () => {
    setTerm('')
    void navigate({ search: (prev) => ({ ...prev, level: undefined }), replace: true })
  }

  if (courses.length === 0) {
    return (
      <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
        <EmptyState
          kind="empty"
          size="page"
          title="No courses yet"
          description="New courses will show up here once they are published."
        />
      </ScreenLayout>
    )
  }

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      <CourseFilters
        levels={levels}
        level={level ?? ALL_LEVELS}
        onLevelChange={setLevel}
        term={term}
        onTermChange={setTerm}
      />
      {filtering ? (
        <CourseResults courses={matches} onClear={clear} />
      ) : (
        <>
          <ContinueLearningShelf resume={home.resume} />
          <FreeToWatchShelf lectures={home.freeToWatch} />
          <CardShelf title="Start here" courses={home.startHere} />
          <CardShelf title="Going deeper" courses={home.goingDeeper} />
          <div>
            <SeeAllLink to="/courses/faq">Questions about courses</SeeAllLink>
          </div>
        </>
      )}
    </ScreenLayout>
  )
}
